<template>
  <div
    class="story-card"
    :style="storyStyles"
    v-touch:touchhold.stop="pauseSlide"
    v-touch:tap.self.prevent="tapSlide"
    v-touch:swipe.prevent="swipeStory"
    v-touch:end="continueTouchhold"
    oncontextmenu="return false;"
  >
    <story-status-bar
      v-if="isActiveStory"
      class="story-card__status-bar"
      :duration="durationMemo[slideKey]"
      :length="slides.length"
      :bar-idx="activeSlideIdx"
      :is-paused="isPausedSlide"
    />
    <button class="story-card__close story-card__nav" v-touch:end.self.prevent="closeStory">
      ×
    </button>
    <button
      :class="['story-card__prev', 'story-card__nav', { 'story-card__nav--hidden': !isActiveStory || hidePrevBtn }]"
      v-touch:end.self="prevSlide"
    >
      &#8249;
    </button>
    <div class="story-card__content story-content">
      <h2 class="story-content__title">{{ slide.title }}</h2>
      <p class="story-content__subtitle" v-html="slide.description"></p>
      <story-background
        :slide="slide"
        :index="slideKey"
        :is-paused="isPausedSlide"
        :is-active-story="isActiveStory"
        :active-slide-idx="activeSlideIdx"
        @duration:set="setDuration"
        @background:loaded="backgroundLoadedHandler"
        @background:waiting="backgroundWaitingHandler"
        @background:error="backgroundErrorHandler"
      />
      <button v-if="slide.button && slide.link" class="story-content__action" v-touch:end="() => redirectTo(slide.link)">
        {{ slide.button }}
      </button>
    </div>
    <button
      :class="['story-card__next', 'story-card__nav', { 'story-card__nav--hidden': !isActiveStory || hideNextBtn }]"
      v-touch:end.self="nextSlide"
    >
      &#8250;
    </button>
  </div>
</template>

<script>
import StoryStatusBar from './StoryStatusBar.vue'
import StoryBackground from './components/StoryBackground.vue'

export default {
  name: 'story-card',
  components: {
    'story-status-bar': StoryStatusBar,
    'story-background': StoryBackground,
  },
  props: {
    currentStoryIdx: {
      type: Number,
      required: true,
    },
    activeStoryIdx: {
      type: Number,
      required: true,
    },
    activeSlideIdx: {
      type: Number,
      required: true,
    },
    isPaused: {
      type: Boolean,
      required: true,
    },
    isStaleHold: {
      type: Boolean,
      required: true,
    },
    hidePrevBtn: {
      type: Boolean,
      default: false,
    },
    hideNextBtn: {
      type: Boolean,
      default: false,
    },
    story: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      durationMemo: {},
      wasTouchhold: false,
      hasWaitingBackground: false,
    }
  },
  computed: {
    storyStyles() {
      const styles = {
        '--scale': this.scale,
        '--translateX': this.translateX,
        '--blur': this.blur,
        zIndex: this.zIndex,
      }

      if (this.slide?.textColor) {
        styles['--slide-text-color'] = this.slide.textColor
      }

      if (this.slide?.gradientColor) {
        styles['--slide-gradient-color'] = this.slide.gradientColor
      }

      if (this.slide?.btnBgColor) {
        styles['--slide-btn-bg-color'] = this.slide.btnBgColor
      }

      if (this.slide?.btnTextColor) {
        styles['--slide-btn-text-color'] = this.slide.btnTextColor
      }

      return styles
    },
    isPausedSlide() {
      return this.isActiveStory ? this.isPaused : true
    },
    offset() {
      return this.currentStoryIdx - this.activeStoryIdx
    },
    scale() {
      if (this.isActiveStory) return 1

      return 0.9 - Math.abs(this.offset) / 10
    },
    translateX() {
      if (this.isActiveStory) return '-50%'

      return `${-50 + this.offset * 30}%`
    },
    blur() {
      return this.isActiveStory ? 0 : '10px'
    },
    zIndex() {
      return 1 - Math.abs(this.offset)
    },
    slides() {
      return this.story?.slides ?? []
    },
    slide() {
      return this.slides[this.slideIdx] ?? {}
    },
    slideIdx() {
      return this.isActiveStory ? this.activeSlideIdx : 0
    },
    textColor() {
      return this.story?.textColor
    },
    isActiveStory() {
      return this.activeStoryIdx === this.currentStoryIdx
    },
    slideKey() {
      return `${this.currentStoryIdx}-${this.slide.id}`
    },
    activeSlide() {
      return this.isActiveStory ? this.activeSlideIdx : null
    },
  },
  methods: {
    setDuration(key, dur) {
      if (this.durationMemo[key]) return

      this.durationMemo = {
        ...this.durationMemo,
        [key]: dur,
      }
    },
    tapSlide() {
      if (this.isStaleHold) {
        this.$emit('set:stale-hold-state', false)
        return
      }

      if (this.isActiveStory) {
        if (this.isPausedSlide) {
          this.continueSlide()
        } else this.nextSlide()
      } else {
        this.selectStory(this.currentStoryIdx)
      }
    },
    prevSlide() {
      this.$emit('slide:prev')
    },
    nextSlide() {
      this.$emit('slide:next')
    },
    pauseSlide(e) {
      if (e.target.nodeName === 'DIV') {
        if (this.currentStoryIdx !== this.activeStoryIdx) {
          this.$emit('set:stale-hold-state', true)
        } else {
          this.wasTouchhold = true
          this.$emit('slide:pause')
        }
      }
    },
    continueSlide() {
      this.$emit('slide:continue')
    },
    prevStory() {
      this.$emit('story:prev')
    },
    nextStory() {
      this.$emit('story:next')
    },
    selectStory(idx) {
      this.$emit('story:select', idx)
    },
    closeStory() {
      if (this.isPausedSlide) this.continueSlide()
      else this.$emit('story:close')
    },
    swipeStory(direction) {
      if (this.isActiveStory) {
        if (direction === 'right') this.prevStory()
        else if (direction === 'left') this.nextStory()
        else this.continueSlide()
      }
    },
    continueTouchhold() {
      if (this.wasTouchhold) {
        this.wasTouchhold = false
        this.continueSlide()
      }
    },
    redirectTo(link) {
      location.href = link
    },
    backgroundWaitingHandler() {
      this.hasWaitingBackground = true
      this.$emit('slide:pause')
    },
    backgroundLoadedHandler() {
      if (this.hasWaitingBackground) {
        this.hasWaitingBackground = false
        this.$emit('slide:continue')
      } else this.$emit('timer:start', this.durationMemo[this.slideKey])
    },
    backgroundErrorHandler() {
      if (!this.isActiveStory) {
        return
      }

      this.$emit('slide:next')
    },
  },
  watch: {
    activeSlide(activeSlide, prevActiveSlide) {
      if (prevActiveSlide === null && activeSlide === 0) {
        this.$emit('timer:start', this.durationMemo[this.slideKey])
      }
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.story-card
  position: absolute
  overflow: hidden
  height: 600px
  width: 500px
  background: #1e2849
  border-radius: 1rem
  cursor: pointer
  top: 50%
  left: 50%
  display: grid
  grid-template-columns: auto 1fr auto
  grid-template-rows: auto 1fr
  grid-template-areas: "s s c" "p m n"
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2)
  will-change: transform
  transform: translate(var(--translateX), -50%) scale(var(--scale))
  transition-duration: 500ms
  transition-timing-function: ease
  transition-property: transform, filter
  filter: blur(var(--blur))
  user-select: none
  -webkit-user-select: none
  -ms-user-select: none

  @media screen and (max-width: 740px)
    width: 80vw
    grid-template-areas: "s s c" "m m m"

  &::before
    content: ""
    position: absolute
    inset: 0
    background: linear-gradient(to top, var(--slide-gradient-color, #1E2849), transparent)
    z-index: -1

  & *
    pointer-events: none

  &__status-bar
    grid-area: s
    align-self: center
    padding-inline: 20px

  &__nav
    pointer-events: auto
    color: white
    font-size: 100px
    align-self: center
    height: 100%
    width: 62px
    user-select: none
    background: transparent
    cursor: pointer
    border: none
    outline: none
    display: flex
    align-items: center
    justify-content: center
    line-height: normal

    transition-duration: 200ms
    transition-property: opacity

    &:hover
      opacity: 0.7

    &--hidden
      visibility: hidden

  &__close
    grid-area: c
    font-weight: bold
    font-size: 36px
    align-self: center
    justify-self: center

  &__content
    grid-area: m
    align-self: flex-end

  &__prev
    grid-area: p

  &__next
    grid-area: n

  &__prev, &__next
    @media screen and (max-width: 740px)
      display: none

.story-content
  color: var(--slide-text-color, #ffffff)
  display: flex
  flex-direction: column
  padding-block-end: 64px
  gap: 1rem

  @media screen and (max-width: 740px)
    width: 100%
    padding-inline: 22px

  &__title
    color: inherit
    font-size: 32px

  &__subtitle
    color: inherit
    font-size: 18px

  &__action
    pointer-events: auto
    margin-top: 2rem
    padding: 1rem
    background-color: var(--slide-btn-bg-color, #00b0bd)
    color: var(--slide-btn-text-color, #ffffff)
    border-radius: .4rem
    text-decoration: none
    font-size: 1.2rem
    font-weight: 500
    text-align: center
</style>



// WEBPACK FOOTER //
// src/templates/components/base/stories/StoryCard.vue